import React from 'react';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store/store';
import { isOrderAvailableAdjastment } from '../helpers';
import { roundToDecimal, roundToEvenDP } from 'lib/helpers';
import {
    ExistingSharedOrderData,
    IAdjustmentLocalResource,
    IUpdateOrderAdjustment
} from 'components/order/model/Order';
import { ExistingSharedBasketData } from 'components/basket/helpers';
import { isNumber, isString } from 'lib/typeInference';
import { IAdjustmentReadResourceV22 } from 'components/basket/model/Basket';

export function useOrderAwards(
    availableAdjustments: IAdjustmentReadResourceV22[] | undefined,
    adjustments: IAdjustmentReadResourceV22[] | undefined,
    orderPrice: number,
    existingParams: ExistingSharedOrderData | ExistingSharedBasketData,
    platformAwards: IUpdateOrderAdjustment[],
    platformAwardsEnabled?: boolean
) {
    const { settings } = useSelector((state: ApplicationState) => state.settings);
    const [selectedReward, setSelectedReward] = React.useState<IAdjustmentLocalResource | undefined>(
        existingParams.selectedRewards?.at(-1) ?? undefined
    );
    const mapAdjustmentToLocalResource = React.useCallback(
        (clientSideCalculation: boolean) =>
            (item: Partial<IAdjustmentLocalResource>): IAdjustmentLocalResource => {
                const itemQuantity = item.quantity ?? 1;
                let fixedQuantity = itemQuantity;
                const chargeDiscountPerUnit = isNumber(item.charges) ? item.charges : 0;
                const valueAfterTax = (item.value ?? 0) + (item.taxToAdd ?? 0) + chargeDiscountPerUnit;
                if (
                    clientSideCalculation &&
                    fixedQuantity > 1 &&
                    orderPrice <= Math.abs(valueAfterTax * (item.quantity ?? 0))
                ) {
                    fixedQuantity = Math.ceil(
                        roundToDecimal(Math.abs(roundToEvenDP(orderPrice / valueAfterTax)))
                    );
                }
                return {
                    ...item,
                    value: item.value ?? 0,
                    quantity: itemQuantity - fixedQuantity === 1 ? itemQuantity : fixedQuantity,
                    totalValue: roundToEvenDP(fixedQuantity * valueAfterTax),
                    selectedOn: item.selectedOn
                };
            },
        [orderPrice]
    );
    const availableAwards: IAdjustmentLocalResource[] = React.useMemo(() => {
        if (!Array.isArray(availableAdjustments)) {
            return [];
        }
        return availableAdjustments
            .filter(item => isOrderAvailableAdjastment(item))
            .map(mapAdjustmentToLocalResource(true));
    }, [availableAdjustments, mapAdjustmentToLocalResource]);
    React.useEffect(() => {
        if (availableAdjustments && selectedReward) {
            const updatedAdjustment = availableAwards.find(item => item.id === selectedReward.id);
            if (!updatedAdjustment) {
                setSelectedReward(undefined);
            }
        }
    }, [availableAwards, availableAdjustments, selectedReward]);
    const handleSetReward = React.useCallback(
        (awardId: string) => {
            const selected = availableAwards.find(item => item.awardId === awardId);
            if (selected) {
                setSelectedReward({ ...selected, selectedOn: Date.now() });
            } else {
                setSelectedReward(selected);
            }
        },
        [availableAwards]
    );
    const handleClearRewards = React.useCallback(() => {
        setSelectedReward(undefined);
    }, []);
    const selectedAdjustments = React.useMemo<IAdjustmentLocalResource[] | undefined>(() => {
        if (!platformAwardsEnabled) {
            if (selectedReward) {
                const availableAdjustment = availableAdjustments?.find(
                    item => item.awardId === selectedReward.awardId
                );
                return [
                    mapAdjustmentToLocalResource(true)({
                        ...availableAdjustment,
                        selectedOn: selectedReward.selectedOn
                    })
                ];
            }
            return undefined;
        }
        const fitleredAwards = adjustments
            ?.filter(
                item =>
                    isString(item.awardId) && !!platformAwards.find(award => award.awardId === item.awardId)
            )
            .map(mapAdjustmentToLocalResource(false));
        if (fitleredAwards?.length) {
            return fitleredAwards.map(item => ({
                ...item,
                selectedOn: platformAwards.find(award => award.awardId === item.awardId)?.selectedOn
            }));
        }
        return undefined;
    }, [
        platformAwardsEnabled,
        adjustments,
        mapAdjustmentToLocalResource,
        selectedReward,
        availableAdjustments,
        platformAwards
    ]);
    const awardsEnabled = React.useMemo(() => !!(settings && !!settings.loyaltyEnabled), [settings]);
    return {
        availableAwards,
        selectedAdjustments,
        handleSetReward,
        handleClearRewards,
        awardsEnabled
    };
}
